import { useId, useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react';

import { Nut } from './Nut.js';
import { LOP_O } from './Truong.js';

/**
 * Form nhập dữ liệu từ file CSV, dùng chung cho mọi màn hình có chức năng này.
 *
 * Gom ở bước 12.2: module `compliance` và `music` có hai file `FormNhapCsv.tsx`
 * gần như giống hệt nhau, chỉ khác bảng cột và một ô chọn nguồn.
 *
 * Trình duyệt đọc file rồi gửi **nội dung dạng chuỗi**; API không nhận multipart
 * (quyết định ở `docs/modules/music.md`).
 */

/** Một cột ánh xạ được: khoá trong mapping, nhãn, và tên cột gợi ý. */
export interface TruongCsv<K extends string> {
  khoa: K;
  nhan: string;
  macDinh: string;
}

export interface FormNhapCsvProps<K extends string> {
  truong: TruongCsv<K>[];
  dangGui: boolean;
  loi?: string | null;
  /** Nhãn nút gửi, ví dụ "Nhập danh mục" hoặc "Nhập sự cố". */
  nhanGui: string;
  /** Nhãn ô chọn file. */
  nhanFile?: string;
  /** Số cột của lưới ánh xạ ở màn rộng. */
  cotAnhXa?: 3 | 4;
  /** Ô chọn thêm đặt cạnh ô file, ví dụ "Nhập vào nguồn" của kho nhạc. */
  truocFile?: ReactNode;
  /** Khối kết quả sau khi nhập xong, mỗi màn tự viết câu chữ của mình. */
  ketQua?: ReactNode;
  onGui: (input: { csv: string; mapping: Partial<Record<K, string>> }) => void;
  onHuy: () => void;
}

export function FormNhapCsv<K extends string>({
  truong,
  dangGui,
  loi,
  nhanGui,
  nhanFile = 'File CSV',
  cotAnhXa = 3,
  truocFile,
  ketQua,
  onGui,
  onHuy,
}: FormNhapCsvProps<K>) {
  const id = useId();
  const [csv, setCsv] = useState('');
  const [tenFile, setTenFile] = useState('');
  const [mapping, setMapping] = useState<Partial<Record<K, string>>>({});
  const [loiDoc, setLoiDoc] = useState<string | null>(null);

  function chonFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    setTenFile(file.name);
    setLoiDoc(null);
    file
      .text()
      .then(setCsv)
      .catch(() => {
        setLoiDoc('Không đọc được file. Hãy lưu lại dạng CSV UTF-8 rồi thử lại.');
      });
  }

  function gui(e: FormEvent) {
    e.preventDefault();
    if (!csv) {
      setLoiDoc('Hãy chọn một file CSV.');
      return;
    }
    onGui({ csv, mapping });
  }

  const oFile = (
    <div>
      <label htmlFor={`${id}-file`} className="mb-1 block text-phu font-medium text-chu-phu">
        {nhanFile}
      </label>
      <input
        id={`${id}-file`}
        type="file"
        accept=".csv,text/csv"
        onChange={chonFile}
        className={`${LOP_O} py-2`}
      />
      {tenFile && <p className="mt-1 text-phu text-chu-mo">Đã chọn: {tenFile}</p>}
    </div>
  );

  return (
    <form onSubmit={gui} className="@container grid gap-4">
      {truocFile ? (
        <div className="grid grid-cols-1 gap-4 @min-[560px]:grid-cols-2">
          {truocFile}
          {oFile}
        </div>
      ) : (
        oFile
      )}

      <fieldset className="rounded-[10px] border border-vien p-4">
        <legend className="px-1 text-phu font-medium text-chu-phu">
          Ánh xạ cột (bỏ trống thì dùng tên gợi ý)
        </legend>
        <div
          className={`grid gap-3 @min-[560px]:grid-cols-2 ${cotAnhXa === 4 ? '@min-[880px]:grid-cols-4' : '@min-[700px]:grid-cols-3'}`}
        >
          {truong.map((t) => (
            <div key={t.khoa}>
              <label
                htmlFor={`${id}-${t.khoa}`}
                className="mb-1 block text-phu font-medium text-chu-phu"
              >
                {t.nhan}
              </label>
              <input
                id={`${id}-${t.khoa}`}
                value={mapping[t.khoa] ?? ''}
                placeholder={t.macDinh}
                onChange={(e) => {
                  const giaTri = e.target.value;
                  setMapping((cu) => ({ ...cu, [t.khoa]: giaTri || undefined }));
                }}
                className={LOP_O}
              />
            </div>
          ))}
        </div>
      </fieldset>

      {(loiDoc ?? loi) && (
        <p role="alert" className="text-co-ban text-bad-chu">
          {loiDoc ?? loi}
        </p>
      )}

      {ketQua}

      <div className="flex justify-end gap-3">
        <Nut kieu="phu" onClick={onHuy}>
          Đóng
        </Nut>
        <Nut kieu="chinh" type="submit" dangChay={dangGui}>
          {nhanGui}
        </Nut>
      </div>
    </form>
  );
}

/** Danh sách dòng lỗi của một lần nhập, cắt bớt cho gọn. */
export function DongLoiCsv({ loi }: { loi: { line: number; message: string }[] }) {
  if (loi.length === 0) return null;
  return (
    <ul className="mt-2 list-disc pl-5 text-bad-chu">
      {loi.slice(0, 5).map((l) => (
        <li key={l.line}>
          Dòng {l.line}: {l.message}
        </li>
      ))}
      {loi.length > 5 && <li>… và {loi.length - 5} dòng khác</li>}
    </ul>
  );
}
